import { Injectable, inject } from '@angular/core';
import {
  catchError,
  delay,
  forkJoin,
  from,
  map,
  Observable,
  of,
  switchMap,
  throwError,
} from 'rxjs';
import { ethers } from 'ethers';
import { DataApiFacade } from '@core/api/data-api.facade';
import type { DataApiProject, DataApiProjectDto } from '@core/api/data-api.facade';
import { IdoSaleApiClient } from '@core/api/ido-sale-api.client';
import { AuthSessionStore } from '@core/state/auth-session.store';
import { AuthProfileStore } from '@core/state/auth-profile.store';
import { toLaunchpadOffering, toLaunchpadOfferings } from '@shared/utils/launchpad-project.mapper';
import type {
  FundingTransaction,
  InvestorContext,
  KycStatus,
  LaunchpadIdoSale,
  LaunchpadOffering,
} from '../models/launchpad.types';
import { buildInvestorContext } from './investor-readiness';
import { withIdoSaleSchedule } from './fundraising-status';
import { isUserProfileComplete } from '@shared/utils/user-profile.util';

/**
 * Launchpad offerings are backed by Data API projects.
 * On-chain IDO buy/claim uses IdoSale (see IdoSaleOnChainService).
 */
export const MOCK_DELAY_MS = 350;

@Injectable({ providedIn: 'root' })
export class LaunchpadApiService {
  private readonly dataApi = inject(DataApiFacade);
  private readonly idoSaleApi = inject(IdoSaleApiClient);
  private readonly authSession = inject(AuthSessionStore);
  private readonly profileStore = inject(AuthProfileStore);

  private readonly investmentsByUser = new Map<string, FundingTransaction[]>();
  private static readonly LOCAL_INVESTMENTS_KEY = 'cgx_launchpad_investments_v1';

  constructor() {
    this.hydrateLocalInvestments();
  }

  getOfferings(): Observable<LaunchpadOffering[]> {
    return this.dataApi.getProjects().pipe(
      map((projects) =>
        toLaunchpadOfferings(projects ?? []).filter(
          (offering) => !offering.listingDraft,
        ),
      ),
      switchMap((offerings) => {
        if (!offerings.length) {
          return of([] as LaunchpadOffering[]);
        }
        return forkJoin(
          offerings.map((offering) => this.attachIdoSale(offering)),
        );
      }),
      delay(MOCK_DELAY_MS),
    );
  }

  getOffering(projectId: number): Observable<LaunchpadOffering> {
    return this.loadOffering(projectId).pipe(
      switchMap((offering) =>
        offering
          ? of(offering)
          : throwError(() => new Error('Offering not found.')),
      ),
      delay(MOCK_DELAY_MS),
    );
  }

  getInvestorContext(
    fundraiseStatus: LaunchpadOffering['fundraiseStatus'],
  ): Observable<InvestorContext> {
    // Wait for AuthProfileStore — a sync read races User GET and falsely
    // marks complete profiles as needs_profile on hard refresh.
    return from(this.profileStore.waitForSettled()).pipe(
      map((session) => {
        const user = session.phase === 'ok' ? session.user : null;
        const profileComplete = isUserProfileComplete(user ?? {});
        const isAuthenticated = this.authSession.isLoggedIn();
        const walletAddress = this.authSession.walletAddress() ?? undefined;
        const kycStatus: KycStatus = user?.hasPassedKyc ? 'approved' : 'none';

        return buildInvestorContext(
          {
            isAuthenticated,
            profileComplete,
            walletConnected: isAuthenticated && !!walletAddress,
            kycStatus,
            fundraiseStatus,
          },
          walletAddress,
        );
      }),
      delay(MOCK_DELAY_MS),
    );
  }

  recordLocalInvestment(tx: FundingTransaction): void {
    this.appendInvestment(tx);
  }

  getMyInvestments(): Observable<FundingTransaction[]> {
    const wallet = this.authSession.walletAddress()?.toLowerCase();
    if (!wallet) {
      return of([]).pipe(delay(MOCK_DELAY_MS));
    }

    return this.getOfferings().pipe(
      switchMap((offerings) => {
        if (!offerings.length) {
          return of([] as FundingTransaction[]);
        }
        return forkJoin(
          offerings.map((offering) =>
            this.buyerInvestmentForOffering(offering, wallet),
          ),
        ).pipe(map((rows) => rows.flat()));
      }),
      map((apiRows) => {
        const local = this.investmentsByUser.get(this.currentUserKey()) ?? [];
        return mergeInvestmentRows(apiRows, local);
      }),
      catchError(() => of([] as FundingTransaction[])),
    );
  }

  private buyerInvestmentForOffering(
    offering: LaunchpadOffering,
    wallet: string,
  ): Observable<FundingTransaction[]> {
    return this.idoSaleApi.listByProject(offering.projectId).pipe(
      switchMap((sales) => {
        const sale = sales?.[0];
        if (!sale) {
          return of([] as FundingTransaction[]);
        }
        return this.idoSaleApi.getById(sale.idoSaleId, true).pipe(
          map((full) => {
            const buyer = full.buyers?.find(
              (b) => b.address.toLowerCase() === wallet,
            );
            if (!buyer) {
              return [] as FundingTransaction[];
            }
            const purchased = BigInt(buyer.purchased || '0');
            const claimed = BigInt(buyer.claimed || '0');
            const status =
              claimed > 0n && claimed >= purchased
                ? ('completed' as const)
                : purchased > 0n
                  ? ('pending' as const)
                  : ('incomplete' as const);
            const tx: FundingTransaction = {
              id: `ido-buyer-${buyer.idoBuyerId}`,
              projectId: offering.projectId,
              projectName: offering.projectName,
              amount: ethers.formatEther(purchased),
              assetCode: 'ETH',
              status,
              createdAt: buyer.createdAt,
              updatedAt: buyer.updatedAt ?? buyer.createdAt,
              purchased: buyer.purchased,
              claimed: buyer.claimed,
              tokenSymbol: offering.tokenSymbol,
            };
            return [tx];
          }),
        );
      }),
      catchError(() => of([] as FundingTransaction[])),
    );
  }

  private loadOffering(projectId: number): Observable<LaunchpadOffering | null> {
    return this.loadProject(projectId).pipe(
      switchMap((project) => {
        if (!project) {
          return of(null);
        }

        return this.dataApi.getBlockchains().pipe(
          switchMap((chains) => {
            const blockchainKeyById = new Map(
              (chains ?? [])
                .filter(
                  (chain) =>
                    chain.blockchainId != null && chain.key != null,
                )
                .map((chain) => [chain.blockchainId!, chain.key!]),
            );
            const base = toLaunchpadOffering(project, { blockchainKeyById });
            if (!base) {
              return of(null);
            }
            return this.attachIdoSale(base);
          }),
          catchError(() => {
            const fallback = toLaunchpadOffering(project);
            return fallback ? this.attachIdoSale(fallback) : of(null);
          }),
        );
      }),
    );
  }

  private attachIdoSale(
    offering: LaunchpadOffering,
  ): Observable<LaunchpadOffering> {
    return this.idoSaleApi.listByProject(offering.projectId).pipe(
      map((sales) => {
        const active = sales?.[0];
        if (!active) {
          return { ...offering, idoSale: null };
        }
        const idoSale: LaunchpadIdoSale = {
          idoSaleId: active.idoSaleId,
          blockchainId: active.blockchainId,
          contractAddress: active.contractAddress,
          tokenAddress: active.tokenAddress,
          saleStart: active.saleStart,
          publicSaleStart: active.publicSaleStart,
          saleEnd: active.saleEnd,
          priceTokensPerEth: active.priceTokensPerEth,
          capTotal: active.capTotal,
          issued: active.issued,
          maxPerBuyer: active.maxPerBuyer,
          status: active.status,
        };
        const issuedNum = Number(active.issued);
        const totalRaised = Number.isFinite(issuedNum)
          ? issuedNum / 1e18
          : offering.totalRaised;
        return withIdoSaleSchedule(
          { ...offering, totalRaised },
          idoSale,
        );
      }),
      catchError(() => of({ ...offering, idoSale: null })),
    );
  }

  private loadProject(
    projectId: number,
  ): Observable<(DataApiProject | DataApiProjectDto) | null> {
    const withId = (
      project: DataApiProject | DataApiProjectDto,
    ): DataApiProject | DataApiProjectDto => ({
      ...project,
      projectId: project.projectId ?? projectId,
    });
    const findInList = (
      projects: readonly DataApiProject[] | null | undefined,
    ): DataApiProject | null => {
      const project =
        projects?.find((entry) => entry.projectId === projectId) ?? null;
      return project ? withId(project) : null;
    };

    return this.dataApi.getProject(projectId).pipe(
      map(withId),
      catchError(() =>
        this.dataApi.getProjectsByCurrentUser().pipe(
          map(findInList),
          catchError(() => this.dataApi.getProjects().pipe(map(findInList))),
        ),
      ),
    );
  }

  private currentUserKey(): string {
    return this.authSession.walletAddress()?.toLowerCase() ?? 'anonymous';
  }

  private appendInvestment(tx: FundingTransaction): void {
    const key = this.currentUserKey();
    const existing = this.investmentsByUser.get(key) ?? [];
    // Replace any prior local/API-shaped row for the same project.
    const withoutDup = existing.filter(
      (row) => row.projectId !== tx.projectId && row.id !== tx.id,
    );
    this.investmentsByUser.set(key, [...withoutDup, tx]);
    this.persistLocalInvestments();
  }

  private hydrateLocalInvestments(): void {
    if (typeof localStorage === 'undefined') {
      return;
    }
    try {
      const raw = localStorage.getItem(
        LaunchpadApiService.LOCAL_INVESTMENTS_KEY,
      );
      if (!raw) {
        return;
      }
      const parsed = JSON.parse(raw) as Record<string, FundingTransaction[]>;
      for (const [key, rows] of Object.entries(parsed)) {
        if (Array.isArray(rows)) {
          this.investmentsByUser.set(key, rows);
        }
      }
    } catch {
      // ignore corrupt cache
    }
  }

  private persistLocalInvestments(): void {
    if (typeof localStorage === 'undefined') {
      return;
    }
    try {
      const payload: Record<string, FundingTransaction[]> = {};
      for (const [key, rows] of this.investmentsByUser.entries()) {
        payload[key] = rows;
      }
      localStorage.setItem(
        LaunchpadApiService.LOCAL_INVESTMENTS_KEY,
        JSON.stringify(payload),
      );
    } catch {
      // ignore quota / private mode
    }
  }
}

/** Prefer API buyer rows; collapse local optimistic duplicates by projectId. */
export function mergeInvestmentRows(
  apiRows: readonly FundingTransaction[],
  localRows: readonly FundingTransaction[],
): FundingTransaction[] {
  const byProject = new Map<number, FundingTransaction>();
  for (const row of localRows) {
    byProject.set(row.projectId, row);
  }
  for (const row of apiRows) {
    byProject.set(row.projectId, row);
  }
  return [...byProject.values()].sort((a, b) =>
    b.updatedAt.localeCompare(a.updatedAt),
  );
}
